import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * Section wrapper w/ viewport-triggered fade-up. Consistent vertical rhythm
 * (24px scale) across every page — swap padding here, everything follows.
 * Reduced-motion respected by framer's own `useReducedMotion` — when active
 * the initial/animate deltas collapse to no visible movement.
 */
export default function Section({ children, className = '', id, chip, title, subtitle }: {
  children: ReactNode;
  className?: string;
  id?: string;
  chip?: string;
  title?: string;
  subtitle?: string;
}) {
  return (
    <section id={id} className={`max-w-6xl mx-auto px-4 sm:px-5 py-14 sm:py-20 md:py-24 ${className}`}>
      {(chip || title || subtitle) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: [0.22, 0.6, 0.36, 1] }}
          className="mb-8 sm:mb-12 md:mb-14 max-w-2xl"
        >
          {chip && <div className="chip mb-4">{chip}</div>}
          {title && <h2 className="h-title text-[1.75rem] sm:text-4xl md:text-5xl font-extrabold leading-tight">{title}</h2>}
          {subtitle && <p className="mt-3 sm:mt-4 text-base sm:text-lg text-[color:var(--color-ink-dim)] leading-relaxed">{subtitle}</p>}
        </motion.div>
      )}
      {children}
    </section>
  );
}

export function Reveal({ children, delay = 0, y = 24 }: { children: ReactNode; delay?: number; y?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 0.6, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
